import { Loader2 } from 'lucide-react';

export function parsePublishTags(value) {
  if (Array.isArray(value)) {
    return value.map((tag) => String(tag || '').trim()).filter(Boolean);
  }
  return String(value || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function tagKey(tag) {
  return String(tag || '').trim().toLocaleLowerCase();
}

function uniqueTags(tags) {
  const out = [];
  const seen = new Set();
  for (const raw of tags || []) {
    const text = String(raw || '').trim();
    if (!text) continue;
    const key = tagKey(text);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
}

export function PublishTagChips({
  value = '',
  suggestions = [],
  onChange,
  onSuggestionsChange,
  onMore,
  moreBusy = false,
  maxSelected = 15,
  emptyHint = 'Autofill using AI adds tags here. Click a chip to include or remove it.',
}) {
  const selected = uniqueTags(parsePublishTags(value));
  const selectedKeys = new Set(selected.map(tagKey));
  const extras = uniqueTags(suggestions).filter((tag) => !selectedKeys.has(tagKey(tag)));

  const emitSelected = (next) => {
    const clipped = uniqueTags(next).slice(0, maxSelected);
    onChange?.(clipped.join(', '));
  };

  const toggle = (tag) => {
    const key = tagKey(tag);
    if (selectedKeys.has(key)) {
      emitSelected(selected.filter((item) => tagKey(item) !== key));
      onSuggestionsChange?.(uniqueTags([tag, ...extras]));
      return;
    }
    if (selected.length >= maxSelected) return;
    emitSelected([...selected, tag]);
    onSuggestionsChange?.(extras.filter((item) => tagKey(item) !== key));
  };

  return (
    <div className="space-y-2" data-testid="publish-tag-chips">
      <div className="flex flex-wrap items-center gap-2 min-h-[42px] rounded-xl border border-white/10 bg-[#0c1018] px-2.5 py-2">
        {selected.map((tag) => (
          <button
            key={`on-${tagKey(tag)}`}
            type="button"
            onClick={() => toggle(tag)}
            title="Remove from upload"
            className="max-w-full truncate rounded-full border border-[#34d399]/50 bg-[#34d399]/15 px-3 py-1 text-[11px] font-medium text-[#d7f8ec] hover:bg-[#34d399]/25 cursor-pointer"
          >
            {tag}
          </button>
        ))}
        {extras.map((tag) => (
          <button
            key={`off-${tagKey(tag)}`}
            type="button"
            onClick={() => toggle(tag)}
            title="Add to upload"
            className="max-w-full truncate rounded-full border border-white/15 bg-transparent px-3 py-1 text-[11px] font-medium text-[#9CA3AF] hover:border-white/30 hover:text-white cursor-pointer"
          >
            {tag}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onMore?.()}
          disabled={moreBusy}
          className="shrink-0 rounded-full border border-white/20 bg-white/[0.06] px-3 py-1 text-[11px] font-semibold text-white hover:bg-white/[0.12] cursor-pointer disabled:opacity-60"
          data-testid="publish-tags-more-btn"
        >
          {moreBusy ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> More
            </span>
          ) : (
            'More'
          )}
        </button>
      </div>
      {!selected.length && !extras.length ? (
        <p className="text-[11px] text-[#8b95a7]">{emptyHint}</p>
      ) : (
        <p className="text-[11px] text-[#8b95a7]">
          {selected.length} selected · click a chip to toggle · More suggests extra tags
        </p>
      )}
    </div>
  );
}
